import type { FieldErrors } from "react-hook-form";

import type { SurveyFormValues } from "../types/survey.types";

type ValidationError = {
  path: string[];
  message: string;
};

const FORM_FIELD_LABELS: Record<string, string> = {
  title: "Survey title",
  description: "Description",
  category: "Category",
  points: "Points",
  endTime: "End time",
  questions: "Questions",
};

const QUESTION_DATA_FIELD_LABELS: Record<string, string> = {
  question: "Question text",
  options: "Options",
  rows: "Grid rows",
  columns: "Grid columns",
  min_label: "Minimum label",
  max_label: "Maximum label",
  min_date: "Minimum date",
  max_date: "Maximum date",
  min_datetime: "Minimum datetime",
  max_datetime: "Maximum datetime",
  yes_label: "Yes label",
  no_label: "No label",
};

const findFirstValidationError = (value: unknown, path: string[] = []): ValidationError | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  if ("message" in value && typeof value.message === "string") {
    return { path, message: value.message };
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    if (key === "ref" || key === "types") {
      continue;
    }

    const validationError = findFirstValidationError(nestedValue, [...path, key]);
    if (validationError) {
      return validationError;
    }
  }

  return undefined;
};

const getQuestionFieldLabel = (path: string[]) => {
  const dataField = path[3];
  const dataFieldLabel = QUESTION_DATA_FIELD_LABELS[dataField];

  if (dataField === "options" && path[4] !== undefined) {
    const optionNumber = Number(path[4]) + 1;
    const optionField = path[5] === "value" ? "value" : "text";
    return `Option ${optionNumber} ${optionField}`;
  }

  return dataFieldLabel ?? "Configuration";
};

const getValidationErrorLabel = (path: string[]) => {
  if (path[0] !== "questions") {
    return FORM_FIELD_LABELS[path[0]] ?? "Survey form";
  }

  if (path[1] === undefined) {
    return FORM_FIELD_LABELS.questions;
  }

  const questionNumber = Number(path[1]) + 1;
  return `Question ${questionNumber} - ${getQuestionFieldLabel(path)}`;
};

const getActionableValidationMessage = (path: string[], message: string) => {
  if (message !== "Invalid input: expected array, received undefined") {
    return message;
  }

  if (path[3] === "rows") {
    return "Missing grid rows. Add at least one row.";
  }

  if (path[3] === "columns") {
    return "Missing grid columns. Add at least one column.";
  }

  if (path[3] === "options") {
    return "Missing options. Add at least one option.";
  }

  return message;
};

export const getFirstSurveyFormErrorMessage = (
  errors: FieldErrors<SurveyFormValues>,
): string | undefined => {
  const validationError = findFirstValidationError(errors);

  if (!validationError) {
    return undefined;
  }

  const label = getValidationErrorLabel(validationError.path);
  const message = getActionableValidationMessage(validationError.path, validationError.message);
  return `${label}: ${message}`;
};
